import React, { useState, useEffect, useRef } from 'react';
import useSWR from 'swr';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import OnOffToggle from './OnOffToggle';
import { Label } from './ui/label';
import ActionButton from './ui/action-button';
import { Shield, Eye, FileText, CheckCircle, Download, AlertTriangle, Lock, History, ChevronRight, FileDown, Check, ShieldCheck, Fingerprint } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';
import TabPageLayout, { PageLoader } from './TabPageLayout';
import FormSection from './FormSection';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import jsPDF from 'jspdf';

// SWR fetcher
const fetcher = async (url) => {
  const response = await api.get(url);
  return response.data;
};

// Rate limiting for PDF exports (1 export per 5 seconds)
const EXPORT_COOLDOWN_MS = 5000;

const WaiversTabClean = () => {
  const [saving, setSaving] = useState(false);
  
  // Waivers are ALWAYS enabled - this is a business requirement
  const [waiversEnabled, setWaiversEnabled] = useState(true);
  const [waiverText, setWaiverText] = useState('');
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [signedWaivers, setSignedWaivers] = useState([]);
  const [selectedWaiver, setSelectedWaiver] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  
  // Version tracking
  const [currentVersion, setCurrentVersion] = useState('1.0');
  const [versionHistory, setVersionHistory] = useState([]);
  const [showVersionHistoryModal, setShowVersionHistoryModal] = useState(false);
  const [selectedHistoryVersion, setSelectedHistoryVersion] = useState(null);
  const [originalWaiverText, setOriginalWaiverText] = useState('');
  
  // Export rate limiting
  const lastExportRef = useRef(0);
  const [exportCooldown, setExportCooldown] = useState(false);
  
  // Integrity hash data from org
  const [currentWaiverHash, setCurrentWaiverHash] = useState(null);
  const [currentWaiverHashedAt, setCurrentWaiverHashedAt] = useState(null);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const waiversPerPage = 10;
  
  // Sorting
  const [sortBy, setSortBy] = useState('recent'); // 'recent' or 'oldest'
  
  // Fetch organization settings with SWR
  const orgId = typeof window !== 'undefined' ? localStorage.getItem('organizationId') : null;
  const { data: orgData, isLoading: loadingOrg, mutate: mutateOrg } = useSWR(
    orgId ? `/organizations/${orgId}` : null,
    fetcher,
    { revalidateOnFocus: true }
  );
  
  // Fetch bookings for signed waivers
  const { data: bookingsData, isLoading: loadingBookings } = useSWR(
    '/bookings',
    fetcher,
    { revalidateOnFocus: true }
  );
  
  const loading = loadingOrg || loadingBookings;
  
  // Update local state when org data is fetched
  useEffect(() => {
    if (orgData) {
      setWaiversEnabled(true); // Always enabled
      setWaiverText(orgData.customWaiverText || '');
      setOriginalWaiverText(orgData.customWaiverText || '');
      setCurrentVersion(orgData.waiverVersion || '1.0');
      setVersionHistory(orgData.waiverVersionHistory || []);
      setCurrentWaiverHash(orgData.currentWaiverIntegrityHash || null);
      setCurrentWaiverHashedAt(orgData.currentWaiverHashedAt || null);
    }
  }, [orgData]);
  
  // Extract signed waivers from bookings
  useEffect(() => {
    if (bookingsData) {
      const waivers = [];
      bookingsData.forEach(trip => {
        if (trip.waiverSignatures && trip.waiverSignatures.length > 0) {
          trip.waiverSignatures.forEach(sig => {
            waivers.push({
              ...sig,
              trip: {
                id: trip.id,
                date: trip.date,
                startTime: trip.startTime,
                confirmationCode: trip.confirmationCode
              }
            });
          });
        }
      });
      setSignedWaivers(waivers);
    }
  }, [bookingsData]);
  
  // Check if waiver text has changed (needs new version)
  const hasWaiverTextChanged = waiverText !== originalWaiverText && originalWaiverText !== '';
  
  // Calculate next version number
  const getNextVersion = () => {
    const [major, minor] = currentVersion.split('.').map(Number);
    if (minor >= 9) {
      return `${major + 1}.0`;
    }
    return `${major}.${minor + 1}`;
  };

  // Helper function to log waiver audit events
  const logWaiverAudit = async (eventType, details = {}) => {
    try {
      const userData = JSON.parse(localStorage.getItem('userData') || '{}');
      await api.post('/activity-log/waiver-audit', {
        eventType,
        performedBy: userData.username || 'Unknown',
        waiverVersion: currentVersion,
        integrityHash: currentWaiverHash,
        ...details
      });
    } catch (error) {
      console.error('Error logging waiver audit event:', error);
      // Don't block the main action if logging fails
    }
  };

  const handleSave = async () => {
    if (waiversEnabled && waiverText.length < 100) {
      toast.error('Waiver text must be at least 100 characters');
      return;
    }

    setSaving(true);
    try {
      const orgId = localStorage.getItem('organizationId');
      
      // Prepare update data
      const updateData = {
        waiversEnabled: waiversEnabled,
        customWaiverText: waiverText
      };
      
      // If waiver text changed, create new version
      if (hasWaiverTextChanged) {
        const newVersion = getNextVersion();
        const now = new Date().toISOString();
        
        // Add current version to history before updating
        const newHistoryEntry = {
          version: currentVersion,
          text: originalWaiverText,
          createdAt: versionHistory.length > 0 
            ? versionHistory[versionHistory.length - 1]?.updatedAt || now
            : now,
          updatedAt: now
        };
        
        updateData.waiverVersion = newVersion;
        updateData.waiverVersionHistory = [...versionHistory, newHistoryEntry];
        
        await api.put(`/organizations/${orgId}`, updateData);
        
        // Log waiver audit: Version created
        await logWaiverAudit('waiver_version_created', {
          previousVersion: currentVersion,
          waiverVersion: newVersion,
          previousHash: currentWaiverHash,
          metadata: {
            textLengthBefore: originalWaiverText.length,
            textLengthAfter: waiverText.length,
            timestamp: now
          }
        });
        
        // Update local state
        setCurrentVersion(newVersion);
        setVersionHistory([...versionHistory, newHistoryEntry]);
        setOriginalWaiverText(waiverText);
        
        toast.success(`Waiver updated to version ${newVersion}!`);
      } else {
        await api.put(`/organizations/${orgId}`, updateData);
        
        // Log waiver audit: Settings changed (no version change)
        await logWaiverAudit('waiver_settings_changed', {
          metadata: { changeType: 'settings_only' }
        });
        
        toast.success('Waiver settings saved successfully!');
      }
      
      // Refresh org data
      mutateOrg();
      
      // Notify setup guide of data change
      window.dispatchEvent(new Event('setupDataChanged'));
    } catch (error) {
      console.error('Error saving waiver settings:', error);
      toast.error('Failed to save waiver settings');
    } finally {
      setSaving(false);
    }
  };

  const viewWaiverDetails = async (waiver) => {
    setSelectedWaiver(waiver);
    setShowDetailModal(true);
    
    // Log activity: Viewing sensitive waiver data
    try {
      await api.post('/activity-log/log-waiver-access', {
        waiverSignatureName: waiver.signatureName,
        tripId: waiver.trip?.id,
        accessedBy: localStorage.getItem('userData') ? JSON.parse(localStorage.getItem('userData')).username : 'Unknown',
        accessReason: 'Admin viewing waiver details'
      });
    } catch (error) {
      console.error('Error logging waiver access:', error);
    }
  };

  if (loading) {
    return <PageLoader />;
  }

  return (
    <TabPageLayout
      title="Waivers"
      subtitle="Configure digital waivers for customer bookings"
      loading={false}
      actions={
        <div className="flex items-center gap-3">
          {hasWaiverTextChanged && (
            <span className="text-xs sm:text-sm text-amber-600 font-medium animate-pulse">
              ⚠️ Unsaved changes
            </span>
          )}
          <ActionButton
            onClick={handleSave}
            loading={saving}
            disabled={waiversEnabled && waiverText.length < 100}
            icon={CheckCircle}
            className={hasWaiverTextChanged ? 'ring-1 ring-amber-400 ring-offset-1' : ''}
          >
            {hasWaiverTextChanged ? 'Save Changes' : 'Save Settings'}
          </ActionButton>
        </div>
      }
    >
      <div className="space-y-6">
        {/* STEP 1: Enable & Configure Waiver */}
        <FormSection
          stepNumber={1}
          title="Waiver Configuration"
          description="Enable waivers and write your custom waiver text"
          color="blue"
        >
          <div className="space-y-4">
            {/* Enable/Disable - Button Style */}
            {/* Waivers are ALWAYS REQUIRED - Cannot be disabled */}
            <div className="flex items-center justify-between p-4 bg-green-50 rounded-md border border-green-200">
              <div>
                <div className="flex items-center gap-2">
                  <Label className="text-sm sm:text-base font-semibold">Waivers Enabled</Label>
                  <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium flex items-center gap-1">
                    <Lock className="w-3 h-3" />
                    Required
                  </span>
                </div>
                <p className="text-xs sm:text-sm text-gray-600 mt-1">
                  Waivers protect your business from liability and reduce chargebacks
                </p>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-green-700">Always On</span>
                <div className="w-11 h-6 bg-green-500 rounded-full flex items-center px-1">
                  <div className="w-4 h-4 bg-white rounded-full ml-auto shadow-sm"></div>
                </div>
              </div>
            </div>

            {/* Warning if no waiver text */}
            {waiverText.length < 100 && (
              <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-md">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-800">Waiver text required</p>
                  <p className="text-xs text-amber-700 mt-1">
                    You must add waiver text (minimum 100 characters) before you can create services or accept bookings.
                  </p>
                </div>
              </div>
            )}

            {/* Waiver Text Editor - Always shown since waivers are always enabled */}
            <div>
              <Label className="text-base font-semibold mb-2 block">
                Custom Waiver Text <span className="text-red-500">*</span>
              </Label>
              
              <Textarea
                value={waiverText}
                onChange={(e) => setWaiverText(e.target.value)}
                placeholder="Enter your waiver text here... (must be at least 100 characters)"
                className="min-h-[200px] text-sm sm:text-base"
              />
              
              {/* Bottom row: Version info (left) | Character count (right) */}
              <div className="flex items-center justify-between mt-2">
                {/* Left: Version Badge & History Link */}
                <div className="flex items-center gap-3">
                  <span className="text-sm text-blue-600 font-medium">
                    v{currentVersion}
                  </span>
                  {hasWaiverTextChanged && (
                    <span className="text-xs text-amber-600 font-medium">
                      → v{getNextVersion()} on save
                    </span>
                  )}
                  {versionHistory.length > 0 && (
                    <button
                      onClick={() => {
                        setShowVersionHistoryModal(true);
                        // Log audit: Version history accessed
                        logWaiverAudit('waiver_history_accessed', {
                          metadata: { totalVersions: versionHistory.length }
                        });
                      }}
                      className="text-sm text-blue-600 hover:text-blue-800 hover:underline transition-colors"
                    >
                      View History ({versionHistory.length})
                    </button>
                  )}
                </div>
                
                {/* Right: Character count */}
                <p className="text-xs sm:text-sm text-gray-500">
                  {waiverText.length} characters
                  {waiverText.length < 100 && waiverText.length > 0 && (
                    <span className="text-red-600 ml-2">
                      ({100 - waiverText.length} more needed)
                    </span>
                  )}
                  {waiverText.length >= 100 && (
                    <span className="text-green-600 ml-2">✓ Minimum met</span>
                  )}
                </p>
              </div>
            </div>
          </div>
        </FormSection>

        {/* STEP 2: Preview Waiver */}
        {waiverText && (
          <FormSection
            stepNumber={2}
            title="Preview Waiver"
            description="See how customers will see your waiver text"
            color="purple"
          >
            <div className="space-y-4">
              {/* Best Practice Notice */}
              <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                <p className="text-sm sm:text-base font-semibold text-blue-900 mb-1">💡 Best Practice: Physical Backup Waivers</p>
                <p className="text-xs sm:text-sm text-blue-800 leading-relaxed">
                  While digital waivers are legally enforceable, we recommend keeping printed physical waivers on-site as backup. You can print this waiver and have customers sign and date it in person if digital systems fail (SMS verification, internet outages, etc.). The printed version includes signature and date fields at the bottom.
                </p>
              </div>
              
              <button
                onClick={() => setShowPreviewModal(true)}
                className="flex items-center gap-2 text-gray-700 opacity-80 hover:opacity-100 font-medium text-lg px-6 py-3 border border-gray-300 rounded-md hover:bg-gray-50 transition-all"
              >
                <Eye className="w-5 h-5" />
                Open Preview
              </button>
            </div>
          </FormSection>
        )}

        {/* STEP 3: Signed Waivers List */}
        <FormSection
          stepNumber={waiverText ? 3 : 2}
          title={`Signed Waivers (${signedWaivers.length})`}
          description="View all customer waiver signatures with device verification"
          color="green"
        >
          {signedWaivers.length === 0 ? (
            <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
              <Shield className="w-12 h-12 mx-auto text-gray-300 mb-2" />
              <p className="text-gray-600">No signed waivers yet</p>
              <p className="text-xs text-gray-500 mt-1">Waivers will appear here after customers sign</p>
            </div>
          ) : (
            <>
              {/* Sort Filter */}
              <div className="flex items-center justify-between mb-4">
                <p className="text-sm text-gray-600">
                  Showing {((currentPage - 1) * waiversPerPage) + 1}-{Math.min(currentPage * waiversPerPage, signedWaivers.length)} of {signedWaivers.length}
                </p>
                <select
                  value={sortBy}
                  onChange={(e) => {
                    setSortBy(e.target.value);
                    setCurrentPage(1); // Reset to page 1 when sorting changes
                  }}
                  className="text-sm border border-gray-300 rounded px-3 py-1"
                >
                  <option value="recent">Most Recent First</option>
                  <option value="oldest">Oldest First</option>
                </select>
              </div>

              {/* Waivers List */}
              <div className="space-y-3">
                {signedWaivers
                  .sort((a, b) => {
                    const dateA = new Date(a.signedAt).getTime();
                    const dateB = new Date(b.signedAt).getTime();
                    return sortBy === 'recent' ? dateB - dateA : dateA - dateB;
                  })
                  .slice((currentPage - 1) * waiversPerPage, currentPage * waiversPerPage)
                  .map((waiver, idx) => (
                    <div 
                      key={idx}
                      className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md hover:border-blue-300 transition-all cursor-pointer"
                      onClick={() => viewWaiverDetails(waiver)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className="font-semibold text-gray-900">{waiver.signatureName}</p>
                            {waiver.waiverVersion && (
                              <span className="px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded text-xs font-medium">
                                v{waiver.waiverVersion}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-600 mt-1">
                            Booking: {waiver.trip?.date} at {waiver.trip?.startTime}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            Signed: {new Date(waiver.signedAt).toLocaleString()}
                          </p>
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          <span className="badge-app badge-app-success">
                            Verified
                          </span>
                          <p className="text-xs text-gray-500">Click for details</p>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
              
              {/* Pagination */}
              {signedWaivers.length > waiversPerPage && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </Button>
                  <span className="text-sm text-gray-600">
                    Page {currentPage} of {Math.ceil(signedWaivers.length / waiversPerPage)}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(p => Math.min(Math.ceil(signedWaivers.length / waiversPerPage), p + 1))}
                    disabled={currentPage >= Math.ceil(signedWaivers.length / waiversPerPage)}
                  >
                    Next
                  </Button>
                </div>
              )}
            </>
          )}
        </FormSection>
      </div>

      {/* Waiver Detail Modal - ALL SENSITIVE DATA */}
      {selectedWaiver && (
        <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-blue-600" />
                Waiver Signature Details
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-6 mt-4">
              {/* Version & Agreement Badge */}
              <div className="flex items-center gap-3">
                <span className="px-3 py-1.5 bg-blue-100 text-blue-800 text-sm font-semibold rounded-full">
                  v{selectedWaiver.waiverVersion || '1.0'}
                </span>
                <span className="px-3 py-1.5 bg-green-100 text-green-800 text-sm font-semibold rounded-full flex items-center gap-1">
                  <Check className="w-3.5 h-3.5" />
                  Agreed to Waiver
                </span>
              </div>
              
              {/* Signer Info - Clean layout */}
              <div className="grid grid-cols-2 gap-x-8 gap-y-4">
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Signer Name</p>
                  <p className="text-base font-semibold text-gray-900">{selectedWaiver.signatureName}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Signed At</p>
                  <p className="text-base font-semibold text-gray-900">{new Date(selectedWaiver.signedAt).toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Booking</p>
                  <p className="text-base font-semibold text-gray-900">
                    {selectedWaiver.trip?.confirmationCode || 'N/A'}
                    {selectedWaiver.trip?.date && <span className="text-gray-500 font-normal"> • {selectedWaiver.trip.date}</span>}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Verification</p>
                  <p className="text-base font-semibold text-gray-900">
                    {selectedWaiver.verificationMethod === 'email' ? 'Email PIN' :
                     selectedWaiver.verificationMethod === 'sms' ? 'SMS PIN' :
                     selectedWaiver.verificationMethod === 'id' ? 'ID Document' :
                     selectedWaiver.verificationMethod === 'team_member_id_check' ? 'Staff ID Check' :
                     'Unknown'}
                  </p>
                </div>
                {(selectedWaiver.verificationMethod === 'email' || selectedWaiver.verificationMethod === 'sms') && (
                  <div className="col-span-2">
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">
                      {selectedWaiver.verificationMethod === 'email' ? 'Verified Email' : 'Verified Phone'}
                    </p>
                    <p className="text-base font-mono text-gray-900">
                      {selectedWaiver.verificationMethod === 'email' 
                        ? (selectedWaiver.email || selectedWaiver.verifiedContact || 'N/A')
                        : (selectedWaiver.phoneNumber || 'N/A')}
                    </p>
                  </div>
                )}
              </div>

              {/* Signature Image */}
              {(() => {
                // Get signature - prefer signatureData (new), fallback to signatureUrl/signatureImage
                const getSignatureSource = () => {
                  // New format: signatureData is compressed JPEG base64
                  if (selectedWaiver.signatureData && selectedWaiver.signatureData.startsWith('data:')) {
                    return selectedWaiver.signatureData;
                  }
                  // Legacy: signatureUrl (file path)
                  const sigUrl = selectedWaiver.signatureUrl || selectedWaiver.signatureDataUrl || selectedWaiver.signatureImage;
                  if (!sigUrl) return null;
                  // If it's a data URL or already has protocol, use as-is
                  if (sigUrl.startsWith('data:') || sigUrl.startsWith('http')) return sigUrl;
                  // Prepend API base URL for relative paths
                  const apiBase = process.env.REACT_APP_BACKEND_URL || '';
                  return `${apiBase}${sigUrl}`;
                };
                const signatureSrc = getSignatureSource();
                
                return signatureSrc ? (
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Digital Signature</p>
                    <div className="border border-gray-200 rounded-lg p-4 bg-white">
                      <img 
                        src={signatureSrc} 
                        alt="Signature" 
                        className="max-h-24 w-auto"
                        onError={(e) => {
                          console.error('Failed to load signature image');
                          e.target.style.display = 'none';
                        }}
                      />
                    </div>
                  </div>
                ) : (
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Digital Signature</p>
                    <div className="border border-dashed border-gray-300 rounded-lg p-4 bg-gray-50 text-center">
                      <p className="text-sm text-gray-500">Signature image not available</p>
                      <p className="text-xs text-gray-400 mt-1">Signed via checkbox agreement</p>
                    </div>
                  </div>
                );
              })()}

              {/* Device Fingerprint - Terminal style */}
              <div className="bg-gray-900 rounded-lg p-4">
                <p className="text-xs text-green-400 font-mono uppercase tracking-wide mb-3">Device Fingerprint</p>
                <div className="space-y-1.5 font-mono text-xs">
                  <div className="flex">
                    <span className="text-gray-500 w-28">IP</span>
                    <span className="text-white">{selectedWaiver.ipAddress || 'Not captured'}</span>
                  </div>
                  <div className="flex">
                    <span className="text-gray-500 w-28">Platform</span>
                    <span className="text-white">{selectedWaiver.deviceInfo?.platform || 'Unknown'}</span>
                  </div>
                  <div className="flex">
                    <span className="text-gray-500 w-28">Screen</span>
                    <span className="text-white">{selectedWaiver.deviceInfo?.screenResolution || 'Unknown'}</span>
                  </div>
                  <div className="flex">
                    <span className="text-gray-500 w-28">Timezone</span>
                    <span className="text-white">{selectedWaiver.deviceInfo?.timezone || 'Unknown'}</span>
                  </div>
                  <div className="flex">
                    <span className="text-gray-500 w-28">Language</span>
                    <span className="text-white">{selectedWaiver.deviceInfo?.language || 'Unknown'}</span>
                  </div>
                  {selectedWaiver.deviceInfo?.userAgent && (
                    <div className="pt-2 border-t border-gray-700 mt-2">
                      <span className="text-gray-500">User Agent</span>
                      <p className="text-green-400 text-[10px] break-all mt-1 leading-relaxed">
                        {selectedWaiver.deviceInfo.userAgent}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Legal Notice - Concise */}
              <p className="text-xs text-gray-500 border-t border-gray-200 pt-4">
                This signature is legally binding. Device data and IP address stored for compliance. Access logged.
              </p>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Waiver Preview Modal */}
      {showPreviewModal && (
        <Dialog open={showPreviewModal} onOpenChange={setShowPreviewModal}>
          <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Waiver Preview</DialogTitle>
            </DialogHeader>
            <div className="mt-4">
              <style>
                {`
                  @media print {
                    body { margin: 0.5in; }
                    #waiver-print-content { 
                      max-width: 100%; 
                      page-break-inside: avoid;
                    }
                    #waiver-print-content h3 { 
                      page-break-after: avoid; 
                    }
                    #waiver-print-content .signature-section {
                      page-break-before: auto;
                      margin-top: 1in;
                    }
                  }
                `}
              </style>
              <div className="bg-white border border-gray-200 rounded-lg p-8" id="waiver-print-content">
                <h3 className="font-bold text-xl mb-6 text-center">Waiver Agreement</h3>
                <div className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap mb-8" style={{ wordWrap: 'break-word', overflowWrap: 'break-word' }}>
                  {waiverText}
                </div>
                
                {/* Signature Fields for Physical Print */}
                <div className="border-t-2 border-gray-300 pt-8 mt-8 signature-section">
                  <p className="text-xs text-gray-600 mb-6">
                    By signing below, I acknowledge that I have read, understood, and agree to the terms of this waiver.
                  </p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                      <p className="text-xs font-semibold text-gray-700 mb-2">PRINT NAME</p>
                      <div className="border-b-2 border-gray-400 h-10"></div>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-gray-700 mb-2">SIGNATURE</p>
                      <div className="border-b-2 border-gray-400 h-10"></div>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-gray-700 mb-2">DATE</p>
                      <div className="border-b-2 border-gray-400 h-10"></div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-6 flex gap-3 justify-end print:hidden">
                <Button variant="outline" onClick={() => setShowPreviewModal(false)}>
                  Close
                </Button>
                <Button onClick={() => window.print()} style={{ backgroundColor: '#2563EB' }} className="hover:opacity-90 text-white">
                  <Download className="w-4 h-4 mr-2" />
                  Print Waiver
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Version History Modal */}
      {showVersionHistoryModal && (
        <Dialog open={showVersionHistoryModal} onOpenChange={setShowVersionHistoryModal}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <History className="w-5 h-5" />
                Waiver Version History
              </DialogTitle>
            </DialogHeader>
            
            {/* Export buttons row */}
            <div className="flex justify-between items-center mb-2 gap-2">
              <p className="text-xs text-gray-500">
                {exportCooldown ? 'Please wait before exporting again...' : 'Export official legal documents'}
              </p>
              <Button
                variant="outline"
                size="sm"
                disabled={exportCooldown}
                onClick={() => {
                  // Rate limiting check
                  const now = Date.now();
                  if (now - lastExportRef.current < EXPORT_COOLDOWN_MS) {
                    toast.error('Please wait a few seconds before exporting again');
                    return;
                  }
                  lastExportRef.current = now;
                  setExportCooldown(true);
                  setTimeout(() => setExportCooldown(false), EXPORT_COOLDOWN_MS);
                  
                  // Helper: Format date with ordinal suffix
                  const formatDateWithOrdinal = (dateStr) => {
                    const date = new Date(dateStr);
                    const day = date.getDate();
                    const suffix = ['th', 'st', 'nd', 'rd'][day % 10 > 3 ? 0 : (day % 100 - day % 10 !== 10 ? day % 10 : 0)];
                    const month = date.toLocaleString('en-US', { month: 'long' });
                    const year = date.getFullYear();
                    const time = date.toLocaleString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
                    const tz = date.toLocaleString('en-US', { timeZoneName: 'short' }).split(' ').pop();
                    return `${month} ${day}${suffix}, ${year} at ${time} ${tz}`;
                  };
                  
                  // Helper: Add waiver text with proper pagination
                  const addFullWaiverText = (doc, text, startY, pageWidth, pageHeight) => {
                    let yPos = startY;
                    const lineHeight = 4;
                    const margin = 20;
                    const maxY = pageHeight - 30; // Leave room for footer
                    
                    doc.setFontSize(9);
                    doc.setFont('helvetica', 'normal');
                    const lines = doc.splitTextToSize(text || 'No waiver text configured.', pageWidth - (margin * 2));
                    
                    for (let i = 0; i < lines.length; i++) {
                      if (yPos > maxY) {
                        doc.addPage();
                        yPos = 20;
                      }
                      doc.text(lines[i], margin, yPos);
                      yPos += lineHeight;
                    }
                    
                    return yPos + 5;
                  };
                  
                  // Create PDF document
                  const doc = new jsPDF();
                  const orgName = localStorage.getItem('organizationName') || 'Organization';
                  const pageWidth = doc.internal.pageSize.getWidth();
                  const pageHeight = doc.internal.pageSize.getHeight();
                  let yPos = 20;
                  
                  // ===== HEADER =====
                  doc.setFontSize(18);
                  doc.setFont('helvetica', 'bold');
                  doc.text('WAIVER VERSION HISTORY', pageWidth / 2, yPos, { align: 'center' });
                  yPos += 8;
                  
                  doc.setFontSize(12);
                  doc.setFont('helvetica', 'normal');
                  doc.text('Official Legal Document - Complete Record', pageWidth / 2, yPos, { align: 'center' });
                  yPos += 15;
                  
                  // ===== DOCUMENT INFO =====
                  doc.setFontSize(10);
                  doc.setFont('helvetica', 'bold');
                  doc.text('Organization:', 20, yPos);
                  doc.setFont('helvetica', 'normal');
                  doc.text(orgName, 55, yPos);
                  yPos += 6;
                  
                  doc.setFont('helvetica', 'bold');
                  doc.text('Generated:', 20, yPos);
                  doc.setFont('helvetica', 'normal');
                  doc.text(formatDateWithOrdinal(new Date().toISOString()), 55, yPos);
                  yPos += 6;
                  
                  doc.setFont('helvetica', 'bold');
                  doc.text('Current Version:', 20, yPos);
                  doc.setFont('helvetica', 'normal');
                  doc.text(`v${currentVersion}`, 55, yPos);
                  yPos += 6;
                  
                  doc.setFont('helvetica', 'bold');
                  doc.text('Total Versions:', 20, yPos);
                  doc.setFont('helvetica', 'normal');
                  doc.text(`${versionHistory.length + 1}`, 55, yPos);
                  yPos += 10;
                  
                  // Integrity hash if available
                  if (currentWaiverHash) {
                    doc.setFontSize(8);
                    doc.setFont('helvetica', 'bold');
                    doc.text('Integrity Hash:', 20, yPos);
                    doc.setFont('courier', 'normal');
                    doc.text(currentWaiverHash.substring(0, 32) + '...', 55, yPos);
                    yPos += 5;
                    doc.setFont('helvetica', 'italic');
                    doc.setTextColor(100, 100, 100);
                    doc.text('HMAC-SHA256 - Tamper-proof verification', 55, yPos);
                    doc.setTextColor(0, 0, 0);
                    yPos += 8;
                  }
                  
                  // ===== CURRENT WAIVER (FULL TEXT) =====
                  doc.setDrawColor(200, 200, 200);
                  doc.line(20, yPos, pageWidth - 20, yPos);
                  yPos += 10;
                  
                  doc.setFontSize(12);
                  doc.setFont('helvetica', 'bold');
                  doc.text(`CURRENT WAIVER (v${currentVersion}) - COMPLETE TEXT`, 20, yPos);
                  yPos += 8;
                  
                  // Add FULL waiver text (no truncation)
                  yPos = addFullWaiverText(doc, waiverText, yPos, pageWidth, pageHeight);
                  
                  doc.setFontSize(8);
                  doc.setFont('helvetica', 'italic');
                  doc.setTextColor(100, 100, 100);
                  doc.text(`${waiverText?.length || 0} characters`, 20, yPos);
                  doc.setTextColor(0, 0, 0);
                  yPos += 10;
                  
                  // ===== VERSION HISTORY (FULL TEXT FOR EACH) =====
                  if (versionHistory.length > 0) {
                    [...versionHistory].reverse().forEach((version) => {
                      // New page for each version for clarity
                      doc.addPage();
                      yPos = 20;
                      
                      // Version header
                      doc.setDrawColor(200, 200, 200);
                      doc.setFillColor(245, 245, 245);
                      doc.rect(20, yPos - 5, pageWidth - 40, 20, 'F');
                      
                      doc.setFontSize(12);
                      doc.setFont('helvetica', 'bold');
                      doc.text(`VERSION ${version.version}`, 25, yPos + 5);
                      
                      doc.setFontSize(9);
                      doc.setFont('helvetica', 'normal');
                      doc.text(formatDateWithOrdinal(version.updatedAt || version.createdAt), 25, yPos + 12);
                      yPos += 25;
                      
                      // Integrity hash if available
                      if (version.integrityHash) {
                        doc.setFontSize(8);
                        doc.setFont('helvetica', 'bold');
                        doc.text('Integrity Hash:', 20, yPos);
                        doc.setFont('courier', 'normal');
                        doc.text(version.integrityHash.substring(0, 32) + '...', 55, yPos);
                        yPos += 5;
                        doc.setFont('helvetica', 'italic');
                        doc.setTextColor(100, 100, 100);
                        doc.text(`${version.hashAlgorithm || 'HMAC-SHA256'} - Verified Authentic`, 55, yPos);
                        doc.setTextColor(0, 0, 0);
                        yPos += 8;
                      }
                      
                      // Full waiver text for this version
                      yPos = addFullWaiverText(doc, version.text, yPos, pageWidth, pageHeight);
                      
                      doc.setFontSize(8);
                      doc.setFont('helvetica', 'italic');
                      doc.setTextColor(100, 100, 100);
                      doc.text(`${version.text?.length || 0} characters`, 20, yPos);
                      doc.setTextColor(0, 0, 0);
                    });
                  }
                  
                  // ===== FOOTER ON ALL PAGES =====
                  const pageCount = doc.internal.getNumberOfPages();
                  for (let i = 1; i <= pageCount; i++) {
                    doc.setPage(i);
                    doc.setFontSize(8);
                    doc.setFont('helvetica', 'normal');
                    doc.setTextColor(128, 128, 128);
                    doc.text(
                      `Official Waiver Record - ${orgName} - Page ${i} of ${pageCount}`,
                      pageWidth / 2,
                      pageHeight - 10,
                      { align: 'center' }
                    );
                    doc.setTextColor(0, 0, 0);
                  }
                  
                  // Save PDF
                  doc.save(`waiver-complete-history-${orgName.replace(/\s+/g, '-').toLowerCase()}-${new Date().toISOString().split('T')[0]}.pdf`);
                  
                  // Log audit: Full history exported
                  logWaiverAudit('waiver_history_exported', {
                    exportFormat: 'PDF',
                    versionsIncluded: versionHistory.length + 1, // history + current
                    metadata: {
                      includesCurrentVersion: true,
                      totalVersions: versionHistory.length + 1
                    }
                  });
                  
                  toast.success('Complete waiver history exported as PDF');
                }}
                className="flex items-center gap-1.5 text-xs"
              >
                <FileDown className="w-4 h-4" />
                Export All Versions (Full Text)
              </Button>
            </div>
            
            <div className="space-y-4">
              {/* Current Version */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-blue-900">v{currentVersion}</span>
                    <span className="px-2 py-1 bg-blue-600 text-white text-xs rounded-full font-medium">
                      Current
                    </span>
                  </div>
                  {/* Export single version button */}
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={exportCooldown}
                    onClick={() => {
                      // Rate limiting
                      const now = Date.now();
                      if (now - lastExportRef.current < EXPORT_COOLDOWN_MS) {
                        toast.error('Please wait before exporting again');
                        return;
                      }
                      lastExportRef.current = now;
                      setExportCooldown(true);
                      setTimeout(() => setExportCooldown(false), EXPORT_COOLDOWN_MS);
                      
                      // Export single version
                      const doc = new jsPDF();
                      const orgName = localStorage.getItem('organizationName') || 'Organization';
                      const pageWidth = doc.internal.pageSize.getWidth();
                      const pageHeight = doc.internal.pageSize.getHeight();
                      let yPos = 20;
                      
                      const formatDateWithOrdinal = (dateStr) => {
                        const date = new Date(dateStr);
                        const day = date.getDate();
                        const suffix = ['th', 'st', 'nd', 'rd'][day % 10 > 3 ? 0 : (day % 100 - day % 10 !== 10 ? day % 10 : 0)];
                        const month = date.toLocaleString('en-US', { month: 'long' });
                        const year = date.getFullYear();
                        const time = date.toLocaleString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
                        const tz = date.toLocaleString('en-US', { timeZoneName: 'short' }).split(' ').pop();
                        return `${month} ${day}${suffix}, ${year} at ${time} ${tz}`;
                      };
                      
                      // Header
                      doc.setFontSize(18);
                      doc.setFont('helvetica', 'bold');
                      doc.text('OFFICIAL WAIVER DOCUMENT', pageWidth / 2, yPos, { align: 'center' });
                      yPos += 8;
                      
                      doc.setFontSize(12);
                      doc.setFont('helvetica', 'normal');
                      doc.text(`Version ${currentVersion} - Complete Legal Text`, pageWidth / 2, yPos, { align: 'center' });
                      yPos += 15;
                      
                      // Document info
                      doc.setFontSize(10);
                      doc.setFont('helvetica', 'bold');
                      doc.text('Organization:', 20, yPos);
                      doc.setFont('helvetica', 'normal');
                      doc.text(orgName, 55, yPos);
                      yPos += 6;
                      
                      doc.setFont('helvetica', 'bold');
                      doc.text('Version:', 20, yPos);
                      doc.setFont('helvetica', 'normal');
                      doc.text(`v${currentVersion}`, 55, yPos);
                      yPos += 6;
                      
                      doc.setFont('helvetica', 'bold');
                      doc.text('Generated:', 20, yPos);
                      doc.setFont('helvetica', 'normal');
                      doc.text(formatDateWithOrdinal(new Date().toISOString()), 55, yPos);
                      yPos += 6;
                      
                      doc.setFont('helvetica', 'bold');
                      doc.text('Status:', 20, yPos);
                      doc.setFont('helvetica', 'normal');
                      doc.setTextColor(0, 128, 0);
                      doc.text('ACTIVE - Current Version', 55, yPos);
                      doc.setTextColor(0, 0, 0);
                      yPos += 10;
                      
                      // Integrity hash
                      if (currentWaiverHash) {
                        doc.setFillColor(240, 253, 244);
                        doc.rect(20, yPos - 3, pageWidth - 40, 18, 'F');
                        doc.setFontSize(8);
                        doc.setFont('helvetica', 'bold');
                        doc.text('TAMPER-PROOF VERIFICATION', 25, yPos + 2);
                        doc.setFont('helvetica', 'normal');
                        doc.text(`Integrity Hash (HMAC-SHA256): ${currentWaiverHash.substring(0, 48)}...`, 25, yPos + 8);
                        doc.setFont('helvetica', 'italic');
                        doc.setTextColor(100, 100, 100);
                        doc.text('This hash proves the document has not been modified since creation.', 25, yPos + 13);
                        doc.setTextColor(0, 0, 0);
                        yPos += 22;
                      }
                      
                      // Divider
                      doc.setDrawColor(200, 200, 200);
                      doc.line(20, yPos, pageWidth - 20, yPos);
                      yPos += 10;
                      
                      // Full waiver text
                      doc.setFontSize(9);
                      doc.setFont('helvetica', 'normal');
                      const lines = doc.splitTextToSize(waiverText || 'No waiver text.', pageWidth - 40);
                      const lineHeight = 4;
                      const maxY = pageHeight - 25;
                      
                      for (let i = 0; i < lines.length; i++) {
                        if (yPos > maxY) {
                          doc.addPage();
                          yPos = 20;
                        }
                        doc.text(lines[i], 20, yPos);
                        yPos += lineHeight;
                      }
                      
                      // Character count
                      yPos += 5;
                      doc.setFontSize(8);
                      doc.setFont('helvetica', 'italic');
                      doc.setTextColor(128, 128, 128);
                      doc.text(`Document length: ${waiverText?.length || 0} characters`, 20, yPos);
                      doc.setTextColor(0, 0, 0);
                      
                      // Footer on all pages
                      const pageCount = doc.internal.getNumberOfPages();
                      for (let i = 1; i <= pageCount; i++) {
                        doc.setPage(i);
                        doc.setFontSize(8);
                        doc.setTextColor(128, 128, 128);
                        doc.text(
                          `Official Legal Document - ${orgName} - v${currentVersion} - Page ${i} of ${pageCount}`,
                          pageWidth / 2,
                          pageHeight - 10,
                          { align: 'center' }
                        );
                        doc.setTextColor(0, 0, 0);
                      }
                      
                      doc.save(`waiver-v${currentVersion}-${orgName.replace(/\s+/g, '-').toLowerCase()}-${new Date().toISOString().split('T')[0]}.pdf`);
                      
                      // Log audit: Current version exported
                      logWaiverAudit('waiver_exported_pdf', {
                        exportFormat: 'PDF',
                        waiverVersion: currentVersion,
                        integrityHash: currentWaiverHash,
                        metadata: { exportType: 'current_version' }
                      });
                      
                      toast.success(`Waiver v${currentVersion} exported as PDF`);
                    }}
                    className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
                  >
                    <FileDown className="w-3.5 h-3.5" />
                    Export
                  </Button>
                </div>
                <p className="text-xs text-blue-700">
                  Active waiver version • Last updated {(() => {
                    const date = new Date(currentWaiverHashedAt || new Date());
                    const day = date.getDate();
                    const suffix = ['th', 'st', 'nd', 'rd'][day % 10 > 3 ? 0 : (day % 100 - day % 10 !== 10 ? day % 10 : 0)];
                    const month = date.toLocaleString('en-US', { month: 'short' });
                    const time = date.toLocaleString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }).toLowerCase();
                    const tz = date.toLocaleString('en-US', { timeZoneName: 'short' }).split(' ').pop();
                    return `${month} ${day}${suffix} ${time} ${tz}`;
                  })()}
                </p>
                {/* Integrity hash display */}
                {currentWaiverHash && (
                  <div className="mt-2 flex items-center gap-1.5 text-xs">
                    <ShieldCheck className="w-3.5 h-3.5 text-green-600" />
                    <span className="text-green-700 font-medium">Tamper-proof</span>
                    <code className="text-gray-500 font-mono text-[10px] bg-white px-1.5 py-0.5 rounded">
                      {currentWaiverHash.substring(0, 16)}...
                    </code>
                  </div>
                )}
              </div>
              
              {/* Previous Versions */}
              {versionHistory.length === 0 ? (
                <div className="text-center py-8 bg-gray-50 rounded-lg">
                  <History className="w-8 h-8 mx-auto text-gray-300 mb-2" />
                  <p className="text-gray-500 text-sm">No previous versions</p>
                  <p className="text-gray-400 text-xs mt-1">Version history will appear here when you make changes</p>
                </div>
              ) : (
                <div className="space-y-3">
                  <h4 className="text-sm font-semibold text-gray-700">Previous Versions (Read-Only)</h4>
                  {[...versionHistory].reverse().map((version, index) => {
                    // Format date with ordinal suffix and timezone
                    const date = new Date(version.updatedAt);
                    const day = date.getDate();
                    const suffix = ['th', 'st', 'nd', 'rd'][day % 10 > 3 ? 0 : (day % 100 - day % 10 !== 10 ? day % 10 : 0)];
                    const month = date.toLocaleString('en-US', { month: 'short' });
                    const time = date.toLocaleString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }).toLowerCase();
                    const tz = date.toLocaleString('en-US', { timeZoneName: 'short' }).split(' ').pop();
                    const formattedDate = `${month} ${day}${suffix} ${time} ${tz}`;
                    
                    // Export single historical version
                    const exportSingleVersion = () => {
                      // Rate limiting
                      const now = Date.now();
                      if (now - lastExportRef.current < EXPORT_COOLDOWN_MS) {
                        toast.error('Please wait before exporting again');
                        return;
                      }
                      lastExportRef.current = now;
                      setExportCooldown(true);
                      setTimeout(() => setExportCooldown(false), EXPORT_COOLDOWN_MS);
                      
                      const doc = new jsPDF();
                      const orgName = localStorage.getItem('organizationName') || 'Organization';
                      const pageWidth = doc.internal.pageSize.getWidth();
                      const pageHeight = doc.internal.pageSize.getHeight();
                      let yPos = 20;
                      
                      const formatDateLong = (dateStr) => {
                        const d = new Date(dateStr);
                        const dy = d.getDate();
                        const suf = ['th', 'st', 'nd', 'rd'][dy % 10 > 3 ? 0 : (dy % 100 - dy % 10 !== 10 ? dy % 10 : 0)];
                        const mo = d.toLocaleString('en-US', { month: 'long' });
                        const yr = d.getFullYear();
                        const tm = d.toLocaleString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
                        const tzn = d.toLocaleString('en-US', { timeZoneName: 'short' }).split(' ').pop();
                        return `${mo} ${dy}${suf}, ${yr} at ${tm} ${tzn}`;
                      };
                      
                      // Header
                      doc.setFontSize(18);
                      doc.setFont('helvetica', 'bold');
                      doc.text('OFFICIAL WAIVER DOCUMENT', pageWidth / 2, yPos, { align: 'center' });
                      yPos += 8;
                      
                      doc.setFontSize(12);
                      doc.setFont('helvetica', 'normal');
                      doc.text(`Version ${version.version} - Historical Record`, pageWidth / 2, yPos, { align: 'center' });
                      yPos += 15;
                      
                      // Document info
                      doc.setFontSize(10);
                      doc.setFont('helvetica', 'bold');
                      doc.text('Organization:', 20, yPos);
                      doc.setFont('helvetica', 'normal');
                      doc.text(orgName, 55, yPos);
                      yPos += 6;
                      
                      doc.setFont('helvetica', 'bold');
                      doc.text('Version:', 20, yPos);
                      doc.setFont('helvetica', 'normal');
                      doc.text(`v${version.version}`, 55, yPos);
                      yPos += 6;
                      
                      doc.setFont('helvetica', 'bold');
                      doc.text('Created:', 20, yPos);
                      doc.setFont('helvetica', 'normal');
                      doc.text(formatDateLong(version.updatedAt || version.createdAt), 55, yPos);
                      yPos += 6;
                      
                      doc.setFont('helvetica', 'bold');
                      doc.text('Status:', 20, yPos);
                      doc.setFont('helvetica', 'normal');
                      doc.setTextColor(128, 128, 128);
                      doc.text('ARCHIVED - Historical Version', 55, yPos);
                      doc.setTextColor(0, 0, 0);
                      yPos += 10;
                      
                      // Integrity hash
                      if (version.integrityHash) {
                        doc.setFillColor(240, 253, 244);
                        doc.rect(20, yPos - 3, pageWidth - 40, 18, 'F');
                        doc.setFontSize(8);
                        doc.setFont('helvetica', 'bold');
                        doc.text('TAMPER-PROOF VERIFICATION', 25, yPos + 2);
                        doc.setFont('helvetica', 'normal');
                        doc.text(`Integrity Hash (${version.hashAlgorithm || 'HMAC-SHA256'}): ${version.integrityHash.substring(0, 48)}...`, 25, yPos + 8);
                        doc.setFont('helvetica', 'italic');
                        doc.setTextColor(100, 100, 100);
                        doc.text('This hash proves the document has not been modified since creation.', 25, yPos + 13);
                        doc.setTextColor(0, 0, 0);
                        yPos += 22;
                      }
                      
                      // Divider
                      doc.setDrawColor(200, 200, 200);
                      doc.line(20, yPos, pageWidth - 20, yPos);
                      yPos += 10;
                      
                      // Full waiver text
                      doc.setFontSize(9);
                      doc.setFont('helvetica', 'normal');
                      const lines = doc.splitTextToSize(version.text || 'No waiver text.', pageWidth - 40);
                      const lineHeight = 4;
                      const maxY = pageHeight - 25;
                      
                      for (let i = 0; i < lines.length; i++) {
                        if (yPos > maxY) {
                          doc.addPage();
                          yPos = 20;
                        }
                        doc.text(lines[i], 20, yPos);
                        yPos += lineHeight;
                      }
                      
                      // Character count
                      yPos += 5;
                      doc.setFontSize(8);
                      doc.setFont('helvetica', 'italic');
                      doc.setTextColor(128, 128, 128);
                      doc.text(`Document length: ${version.text?.length || 0} characters`, 20, yPos);
                      doc.setTextColor(0, 0, 0);
                      
                      // Footer on all pages
                      const pageCount = doc.internal.getNumberOfPages();
                      for (let i = 1; i <= pageCount; i++) {
                        doc.setPage(i);
                        doc.setFontSize(8);
                        doc.setTextColor(128, 128, 128);
                        doc.text(
                          `Historical Record - ${orgName} - v${version.version} - Page ${i} of ${pageCount}`,
                          pageWidth / 2,
                          pageHeight - 10,
                          { align: 'center' }
                        );
                        doc.setTextColor(0, 0, 0);
                      }
                      
                      doc.save(`waiver-v${version.version}-${orgName.replace(/\s+/g, '-').toLowerCase()}-archived.pdf`);
                      
                      // Log audit: Historical version exported
                      logWaiverAudit('waiver_exported_pdf', {
                        exportFormat: 'PDF',
                        waiverVersion: version.version,
                        integrityHash: version.integrityHash,
                        metadata: { exportType: 'historical_version' }
                      });
                      
                      toast.success(`Waiver v${version.version} exported as PDF`);
                    };
                    
                    return (
                      <div 
                        key={index}
                        className="bg-gray-50 border border-gray-200 rounded-lg p-4 hover:bg-gray-100 transition-colors"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-gray-800">v{version.version}</span>
                            {version.integrityHash && (
                              <span className="flex items-center gap-1 text-[10px] text-green-600">
                                <Fingerprint className="w-3 h-3" />
                                Verified
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-500">{formattedDate}</span>
                            <Button
                              variant="ghost"
                              size="sm"
                              disabled={exportCooldown}
                              onClick={exportSingleVersion}
                              className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1 h-6 px-2"
                            >
                              <FileDown className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <button
                            onClick={() => setSelectedHistoryVersion(selectedHistoryVersion === version.version ? null : version.version)}
                            className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                          >
                            <Eye className="w-4 h-4" />
                            {selectedHistoryVersion === version.version ? 'Hide' : 'View'} waiver text
                          </button>
                        </div>
                        
                        {selectedHistoryVersion === version.version && (
                          <div className="mt-3 p-4 bg-white border border-gray-300 rounded-md">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <Lock className="w-4 h-4 text-gray-400" />
                                <span className="text-xs text-gray-500 font-medium">READ-ONLY</span>
                              </div>
                              {version.integrityHash && (
                                <code className="text-[10px] text-gray-400 font-mono bg-gray-100 px-1.5 py-0.5 rounded">
                                  Hash: {version.integrityHash.substring(0, 12)}...
                                </code>
                              )}
                            </div>
                            <p className="text-sm text-gray-700 whitespace-pre-wrap max-h-48 overflow-y-auto">
                              {version.text}
                            </p>
                            <p className="text-xs text-gray-400 mt-2">
                              {version.text?.length || 0} characters
                            </p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              
              {/* Legal Notice with integrity explanation */}
              <div className="mt-4 pt-3 border-t border-gray-200">
                <p className="text-xs text-gray-500">
                  Previous versions are preserved for legal compliance and cannot be modified.
                </p>
                <div className="mt-2 flex items-start gap-2 text-xs text-gray-400">
                  <ShieldCheck className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                  <p>
                    <span className="font-medium text-gray-600">Tamper-proof integrity:</span> Each version is cryptographically signed with HMAC-SHA256. 
                    If any content is modified in the database, the integrity verification will fail, providing legal proof of authenticity.
                  </p>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </TabPageLayout>
  );
};

export default WaiversTabClean;
